import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Paper,
  useTheme,
  alpha,
  Typography,
  Button,
  Link as MuiLink,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepConnector
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  AttachMoney as CostIcon,
  VerifiedUser as VerifiedUserIcon,
  Description as DescriptionIcon,
  LocalDrink as LocalDrinkIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  LocalShipping as LocalShippingIcon,
  Factory as   FactoryIcon,
  BookmarkBorder as BookmarkBorderIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Summarize as SummarizeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import BatchOverview from './BatchOverview';
import BatchIngredients from './BatchIngredients';
import BatchCosts from './BatchCosts';
import BatchLogs from './BatchLogs';
import BatchSummary from './BatchSummary';
import { supabase } from '../../../supabaseClient';

// Styled components matching FormulationTabs patterns
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(4),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

// Custom Timeline Connector
const TimelineConnector = styled(StepConnector)(({ theme, active, completed }) => ({
  '& .MuiStepConnector-line': {
    height: 4,
    border: 0,
    backgroundColor: completed ? '#4caf50' : active ? '#ff9800' : '#e0e0e0',
    borderRadius: 2,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::after': completed ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
      animation: 'shimmer 2s infinite'
    } : {}
  },
  '@keyframes shimmer': {
    '0%': { left: '-100%' },
    '100%': { left: '100%' }
  }
}));

// Custom Timeline Step Icon
const TimelineStepIcon = styled('div')(({ theme, active, completed, stepIndex }) => {
  const colors = [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main, '#9c27b0'];
  const bgColor = completed ? theme.palette.success.main : active ? colors[stepIndex] : '#e0e0e0';
  
  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: bgColor,
    border: active ? `3px solid ${alpha(colors[stepIndex] || '#1976d2', 0.3)}` : completed ? `3px solid ${theme.palette.success.main}` : '3px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: completed || active ? 'white' : '#999',
    transition: 'all 0.3s ease',
    position: 'relative',
    cursor: 'pointer',
    boxShadow: active ? `0 0 20px ${alpha(colors[stepIndex] || '#1976d2', 0.4)}` : completed ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` : '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `0 6px 20px ${alpha(bgColor || '#e0e0e0', 0.4)}`
    },
    '& .MuiSvgIcon-root': {
      fontSize: 28
    }
  };
});

// Timeline Component
function BatchTimeline({ activeStep, onStepClick, batch, costAnalysisCompleted, batchIngredients, productionCompleted, canAccessOverview, isCreatingNew, availableTabs }) {
  const theme = useTheme();
  const { canAccessPage } = usePermissions();
  
  const allTimelineSteps = [
    {
      label: 'Planning',
      sublabel: 'Batch Overview',
      icon: <AssignmentIcon />,
      description: 'Define batch parameters and specifications',
      completed: !!(batch?.id),
      permission: 'batch_overview'
    },
    {
      label: 'Preparation',
      sublabel: 'Ingredients Setup',
      icon: <ScienceIcon />,
      description: 'Configure ingredients and quantities',
      completed: !!(batch?.id && batchIngredients && batchIngredients.length > 0 && 
                    batchIngredients.every(ing => 
                      ing.planned_amount && 
                      ing.actual_amount && 
                      (ing.supplier_lot_number || ing.supplier_batch_number) &&
                      ing.planned_unit &&
                      ing.actual_unit
                    )),
      permission: 'batch_ingredients'
    },
    {
      label: 'Analysis',
      sublabel: 'Cost Calculation',
      icon: <CostIcon />,
      description: 'Review costs and profitability',
      completed: costAnalysisCompleted,
      permission: 'batch_costs'
    },
    {
      label: 'Production',
      sublabel: 'Mixing Process',
      icon: <BookmarkBorderIcon />,
      description: 'Execute mixing and quality control',
      completed: productionCompleted,
      permission: 'batch_logs',
      hasAccess: () => {
        return canAccessPage('batch_logs_overview') || 
               canAccessPage('batch_logs_premix') || 
               canAccessPage('batch_logs_ingredients') || 
               canAccessPage('batch_logs_qc_checks') || 
               canAccessPage('batch_logs_deviations') || 
               canAccessPage('batch_logs_yield') || 
               canAccessPage('batch_logs_signatures');
      }
    },
    {
      label: 'Summary',
      sublabel: 'Final Review',
      icon: <SummarizeIcon />,
      description: 'Complete batch summary and documentation',
      completed: !!(batch?.status === 'approved' || batch?.status === 'completed'),
      permission: 'batch_summary'
    }
  ];

  // Filter timeline steps based on available tabs
  const timelineSteps = allTimelineSteps.filter(step => {
    if (step.hasAccess) {
      return step.hasAccess();
    }
    return availableTabs.some(tab => tab.permission === step.permission);
  });

  return (
    <Box sx={{ 
      p: 4,
      borderRadius: 4,
      mb: 4,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
            Production Timeline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Track your progress through the batch creation process
          </Typography>
        </Box>
        <Box sx={{ 
          px: 3, 
          py: 1, 
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Step {activeStep + 1} of {timelineSteps.length}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 4, 
        borderRadius: 3, 
        mb: 4, 
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[400], 0.9)}`
      }}>
        <Stepper 
          activeStep={activeStep} 
          connector={<TimelineConnector />}
          sx={{ 
            '& .MuiStepLabel-root': {
              cursor: 'pointer'
            }
          }}
        >
          {timelineSteps.map((step, index) => {
            const isActive = index === activeStep;
            const isCompleted = step.completed;
            
            return (
              <Step 
                key={step.label} 
                onClick={() => {
                  // Only block navigation for NEW batches without overview permissions
                  if (isCreatingNew && index > 0 && !canAccessOverview) {
                    return;
                  }
                  onStepClick(index);
                }}
                sx={{
                  cursor: (isCreatingNew && index > 0 && !canAccessOverview) ? 'not-allowed' : 'pointer',
                  opacity: (isCreatingNew && index > 0 && !canAccessOverview) ? 0.5 : 1
                }}
              >
                <StepLabel
                  StepIconComponent={() => (
                    <TimelineStepIcon 
                      active={isActive} 
                      completed={isCompleted}
                      stepIndex={index}
                    >
                      {isCompleted ? <CheckCircleIcon /> : step.icon}
                    </TimelineStepIcon>
                  )}
                  sx={{
                    '& .MuiStepLabel-labelContainer': {
                      mt: 2
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: isCompleted ? '#4caf50' : isActive ? 'primary.main' : 'text.secondary',
                        mb: 0.5,
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isCompleted ? '#4caf50' : isActive ? 'primary.main' : 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      {step.sublabel}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        display: 'block',
                        maxWidth: 140,
                        lineHeight: 1.3
                      }}
                    >
                      {step.description}
                    </Typography>
                    
                    {/* Status Indicator */}
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: alpha(isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : theme.palette.grey[500], 0.1),
                          borderRadius: 1,
                          border: `1px solid ${alpha(isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : theme.palette.grey[500], 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isCompleted ? (
                          '✓ Complete'
                        ) : (
                          <>
                            {isActive && <ScheduleIcon sx={{ fontSize: 12 }} />}
                            {isActive ? 'In Progress' : 'Pending'}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Progress Bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Overall Progress
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
            {Math.round((timelineSteps.filter(s => s.completed).length / timelineSteps.length) * 100)}%
          </Typography>
        </Box>
        <Box sx={{ 
          width: '100%', 
          height: 8, 
          backgroundColor: alpha(theme.palette.grey[400], 0.3),
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative'
        }}>
          <Box sx={{
            width: `${(timelineSteps.filter(s => s.completed).length / timelineSteps.length) * 100}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: 20,
              height: '100%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 100%)',
              animation: 'progressShine 2s infinite'
            }
          }} />
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => {
            const prevStep = Math.max(0, activeStep - 1);
            onStepClick(prevStep);
          }}
          disabled={activeStep === 0}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            borderColor: 'primary.main',
            color: 'primary.main',
            fontWeight: 500,
            background: 'white',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'white',
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            '&:disabled': {
              borderColor: alpha(theme.palette.grey[400], 0.5),
              color: 'text.disabled',
              backgroundColor: 'white'
            },
            transition: 'all 0.2s ease'
          }}
        >
          ← Previous Step
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {timelineSteps.map((_, index) => (
            <Box
              key={index}
              onClick={() => onStepClick(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: index === activeStep ? 'primary.main' : index < activeStep ? 'success.main' : alpha(theme.palette.grey[400], 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.3)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={() => {
            const nextStep = Math.min(timelineSteps.length - 1, activeStep + 1);
            onStepClick(nextStep);
          }}
          disabled={activeStep === timelineSteps.length - 1 || (isCreatingNew && !canAccessOverview)}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white',
            fontWeight: 500,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              transform: 'translateY(-1px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
            },
            '&:disabled': {
              background: alpha(theme.palette.grey[400], 0.5),
              color: 'text.disabled'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Next Step →
        </Button>
      </Box>
    </Box>
  );
}

const BatchTabs = forwardRef(({ 
  batch, 
  formulations,
  products,
  rawMaterials,
  showSnackbar,
  user,
  userCompanyId,
  onUpdate,
  onBackToList,
  batchTimeFormat = '12hour'
}, ref) => {
  const { companyId } = useParams();
  const theme = useTheme();
  const { canAccessPage, canEditPage } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [costAnalysisCompleted, setCostAnalysisCompleted] = useState(false);
  const [batchIngredients, setBatchIngredients] = useState([]);
  const [productionCompleted, setProductionCompleted] = useState(false);

  // Simple check batch log completion from database flag
  const checkBatchLogCompletionFromDB = async () => {
    if (!batch?.id) {
      setProductionCompleted(false);
      return;
    }

    try {
      // Just fetch the simple boolean flag from database
      const { data: logData, error: logError } = await supabase
        .from('batch_logs')
        .select('batch_log_completed')
        .eq('batch_id', batch.id)
        .single();

      if (logError && logError.code !== 'PGRST116') {
        console.error('Error loading batch log completion:', logError);
        setProductionCompleted(false);
        return;
      }

      // Use the simple boolean flag
      const isCompleted = logData?.batch_log_completed || false;
      setProductionCompleted(isCompleted);

    } catch (error) {
      console.error('Error checking batch log completion from DB:', error);
      setProductionCompleted(false);
    }
  };

  // Load batch ingredients and check cost completion when batch changes
  useEffect(() => {
    const loadBatchData = async () => {
      if (!batch?.id) {
        setBatchIngredients([]);
        setCostAnalysisCompleted(false);
        setProductionCompleted(false);
        return;
      }

      try {
        // Load batch ingredients
        const { data: ingredientsData, error: ingredientsError } = await supabase
          .from('batch_ingredients')
          .select(`
            *,
            raw_materials:raw_material_id (
              id, name, cas_number, sku, category, 
              material_type, primary_unit_measure, color, form,
              density_g_ml, primary_unit_type, primary_unit_quantity
            ),
            formulation_phases:formulation_phase_id (
              id, name, sequence
            )
          `)
          .eq('batch_id', batch.id)
          .order('sequence_number');

        if (ingredientsError) {
          console.error('Error loading batch ingredients:', ingredientsError);
        } else {
          setBatchIngredients(ingredientsData || []);
        }

        // Check if cost analysis is complete by looking for saved cost data
        const { data: costData, error: costError } = await supabase
          .from('batch_costs')
          .select('id, total_cost, cost_per_unit')
          .eq('batch_id', batch.id)
          .single();

        if (costError && costError.code !== 'PGRST116') {
          console.error('Error loading batch costs:', costError);
        } else {
          // Mark as complete if cost data exists and has meaningful values
          setCostAnalysisCompleted(!!(costData && costData.total_cost > 0));
        }

        // Check batch log completion status from database
        await checkBatchLogCompletionFromDB();
      } catch (error) {
        console.error('Error loading batch data:', error);
      }
    };

    loadBatchData();
  }, [batch?.id]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openEditDialog: (isCreatingNew = false) => {
      if (activeTab === 0) {
        // If we're on overview tab, call the edit dialog directly
        // This will be handled by the BatchOverview component
      } else {
        // Switch to overview tab first, then open edit dialog
        setActiveTab(0);
        setTimeout(() => {
          // The BatchOverview should handle this
        }, 100);
      }
    }
  }));

  const handleTimelineStepClick = (stepIndex) => {
    // Block navigation only for NEW batches if user doesn't have overview permissions
    const isCreatingNew = !batch?.id;
    if (isCreatingNew && stepIndex > 0 && (!canAccessPage('batch_overview') || !canEditPage('batch_overview'))) {
      return;
    }
    
    // Map the timeline step index to the actual tab index in filtered tabsData
    if (stepIndex >= 0 && stepIndex < tabsData.length) {
      setActiveTab(stepIndex);
    }
  };

  const handleCostAnalysisSave = () => {
    // Trigger refresh to check cost completion status from database
    handleBatchIngredientsUpdate();
  };

  const handleProductionCompletion = async (isCompleted, completionStatus) => {
    // Update state immediately - no debouncing needed
    setProductionCompleted(isCompleted);
  };

  const handleBatchIngredientsUpdate = () => {
    // Re-load batch data (ingredients and costs) when they are updated
    if (batch?.id) {
      const loadBatchData = async () => {
        try {
          // Load batch ingredients
          const { data: ingredientsData, error: ingredientsError } = await supabase
            .from('batch_ingredients')
            .select(`
              *,
              raw_materials:raw_material_id (
                id, name, cas_number, sku, category, 
                material_type, primary_unit_measure, color, form,
                density_g_ml, primary_unit_type, primary_unit_quantity
              ),
              formulation_phases:formulation_phase_id (
                id, name, sequence
              )
            `)
            .eq('batch_id', batch.id)
            .order('sequence_number');

          if (ingredientsError) {
            console.error('Error reloading batch ingredients:', ingredientsError);
          } else {
            setBatchIngredients(ingredientsData || []);
          }

          // Re-check cost analysis completion
          const { data: costData, error: costError } = await supabase
            .from('batch_costs')
            .select('id, total_cost, cost_per_unit')
            .eq('batch_id', batch.id)
            .single();

          if (costError && costError.code !== 'PGRST116') {
            console.error('Error reloading batch costs:', costError);
          } else {
            setCostAnalysisCompleted(!!(costData && costData.total_cost > 0));
          }

          // Also refresh batch log completion status
          await checkBatchLogCompletionFromDB();
        } catch (error) {
          console.error('Error reloading batch data:', error);
        }
      };

      loadBatchData();
    }
  };

  const allTabsData = [
    {
      label: 'Overview',
      icon: <AssignmentIcon />,
      permission: 'batch_overview',
      component: (
        <BatchOverview 
          batch={batch}
          formulations={formulations}
          products={products}
          showSnackbar={showSnackbar}
          isCreatingNew={!batch?.id}
          userCompanyId={userCompanyId}
          onUpdate={(updatedBatch) => {
            onUpdate(updatedBatch);
            handleBatchIngredientsUpdate();
          }}
          onBackToList={onBackToList}
          user={user}
        />
      )
    },
    {
      label: 'Ingredients',
      icon: <ScienceIcon />,
      permission: 'batch_ingredients',
      component: (
        <BatchIngredients 
          batch={batch}
          rawMaterials={rawMaterials}
          formulations={formulations}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          onUpdate={(updatedBatch) => {
            onUpdate(updatedBatch);
            handleBatchIngredientsUpdate();
          }}
          user={user}
        />
      )
    },
    {
      label: 'Cost Analysis',
      icon: <CostIcon />,
      permission: 'batch_costs',
      component: (
        <BatchCosts 
          batch={batch}
          rawMaterials={rawMaterials}
          formulations={formulations}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          onUpdate={(updatedBatch) => {
            onUpdate(updatedBatch);
            handleBatchIngredientsUpdate();
          }}
          user={user}
          onSave={handleCostAnalysisSave}
        />
      )
    },
    {
      label: 'Mixing Log',
      icon: <BookmarkBorderIcon />,
      permission: 'batch_logs',
      hasAccess: () => {
        return canAccessPage('batch_logs_overview') || 
               canAccessPage('batch_logs_premix') || 
               canAccessPage('batch_logs_ingredients') || 
               canAccessPage('batch_logs_qc_checks') || 
               canAccessPage('batch_logs_deviations') || 
               canAccessPage('batch_logs_yield') || 
               canAccessPage('batch_logs_signatures');
      },
      component: (
        <BatchLogs 
          batchId={batch?.id}
          batchData={batch}
          onProductionCompletion={handleProductionCompletion}
          user={user}
        />
      )
    },
    {
      label: 'Summary',
      icon: <SummarizeIcon />,
      permission: 'batch_summary',
      component: (
        <BatchSummary 
          batch={batch}
          onPrint={() => window.print()}
          onExport={() => {
            // Export functionality can be implemented later
            showSnackbar('Export functionality coming soon', 'info');
          }}
          onUpdate={onUpdate}
          showSnackbar={showSnackbar}
          user={user}
        />
      )
    }
  ];

  // Filter tabs based on permissions
  const tabsData = allTabsData.filter(tab => {
    if (tab.hasAccess) {
      return tab.hasAccess();
    }
    return canAccessPage(tab.permission);
  });

  // Auto-select first available tab if current tab is not accessible (only on mount or when tabs change)
  useEffect(() => {
    if (tabsData.length > 0 && activeTab === 0) {
      // Only redirect if we're on the first tab and it's not accessible
      const currentTab = tabsData[0];
      if (currentTab) {
        const hasAccess = currentTab.hasAccess ? currentTab.hasAccess() : canAccessPage(currentTab.permission);
        if (!hasAccess && tabsData.length > 1) {
          // Find first accessible tab
          const firstAccessibleIndex = tabsData.findIndex(tab => 
            tab.hasAccess ? tab.hasAccess() : canAccessPage(tab.permission)
          );
          if (firstAccessibleIndex >= 0) {
            setActiveTab(firstAccessibleIndex);
          }
        }
      }
    }
  }, [tabsData.length]);

  // If no tabs are available, show access restricted message
  if (tabsData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 8,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 600,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
            opacity: 0.7,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <LocalDrinkIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Batch Management Permissions Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have permissions to access any batch management sections. Contact your system administrator to request access to batch overview, ingredients, costs, logs, or summary.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Page Header */}
      <Card sx={{ 
        borderRadius: 3,
        mb: 4,
        mt: 8,
        border: `1px solid ${theme.palette.grey[400]}`,
        background: theme.palette.grey[100],
        backdropFilter: 'blur(20px)',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
          opacity: 0.8
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
              }}>
                <LocalDrinkIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ 
                  fontWeight: 500, 
                  color: 'primary.main', 
                  mb: 1,
                  fontSize: '2.5rem',
                  letterSpacing: '-0.5px'
                }}>
                  {batch?.batch_number || 'New Production Batch'}
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1.1rem'
                }}>
                  Batch Production
                </Typography>
              </Box>
            </Box>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={onBackToList}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: 'grey.400',
                color: 'primary.main',
                borderWidth: 1,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                }
              }}
            >
              Back to Batches
            </Button>
          </Box>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            maxWidth: 700, 
            lineHeight: 1.6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            Plan, track, and manage production batches from formulation to completion with comprehensive monitoring.
          </Typography>
        </CardContent>
      </Card>

      {/* Enhanced Timeline Component */}
      <BatchTimeline 
        activeStep={activeTab} 
        onStepClick={handleTimelineStepClick}
        batch={batch}
        costAnalysisCompleted={costAnalysisCompleted}
        batchIngredients={batchIngredients}
        productionCompleted={productionCompleted}
        canAccessOverview={canAccessPage('batch_overview') && canEditPage('batch_overview')}
        isCreatingNew={!batch?.id}
        availableTabs={tabsData}
      />

      {/* Enhanced Main Content Area */}
      <SectionPaper>
        {tabsData[activeTab]?.component}
      </SectionPaper>

      <style>
        {`
          @keyframes progressShine {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
});

BatchTabs.displayName = 'BatchTabs';

export default BatchTabs; 